import shortid from 'shortid'
import { addEvent, addActiveToast, setFatalModal, setConfirmationToast, clearConfirmationToast } from '../store/notifications-store.js'

/**
 * Notification Service - Core API for notifications
 * 
 * Usage:
 * - notify.info(message)
 * - notify.success(message)
 * - notify.error(message, details?)
 * - notify.fatal(message, details?)
 */

/**
 * Create a notification event
 * @param {string} level - 'info' | 'error' | 'fatal'
 * @param {string} message - Notification message
 * @param {any} details - Optional details object
 * @returns {Object} Event object
 */
function createEvent(level, message, details = null) {
    return {
        id: shortid.generate(),
        timestamp: new Date().toISOString(),
        level,
        message,
        details: details || undefined
    }
}

/**
 * Show an info notification
 * @param {string} message - Info message
 * @returns {Object} Event object with id
 */
export function info(message) {
    const event = createEvent('info', message)
    addEvent(event)
    addActiveToast(event.id)
    return event
}

/**
 * Show a success notification
 * @param {string} message - Success message
 * @returns {Object} Event object with id
 */
export function success(message) {
    const event = createEvent('success', message)
    addEvent(event)
    addActiveToast(event.id)
    return event
}

/**
 * Show an error notification
 * @param {string} message - Error message
 * @param {any} details - Optional error details
 * @returns {Object} Event object with id
 */
export function error(message, details = null) {
    // A refusal already said why in a sentence; the caller's own
    // "Failed to …" that follows it would only repeat it less clearly.
    if (Date.now() < refusalUntil) return createEvent('error', message, details)
    const event = createEvent('error', message, details)
    addEvent(event)
    addActiveToast(event.id) // Error toasts persist (no auto-dismiss)
    return event
}

let refusalUntil = 0

/**
 * Show why the server refused a request (a sentence from the server), and
 * hold back other error toasts for a moment so it is the only one.
 * @param {string} message
 */
export function refusal(message) {
    refusalUntil = 0
    const event = error(message)
    refusalUntil = Date.now() + 2000
    return event
}

/**
 * Show a fatal error notification
 * @param {string} message - Fatal error message
 * @param {any} details - Optional error details
 */
export function fatal(message, details = null) {
    const event = createEvent('fatal', message, details)
    addEvent(event)
    setFatalModal(event) // Show blocking modal
    addActiveToast(event.id) // Also show toast
}

/**
 * Show a confirmation toast (blocking)
 * @param {string} message - Confirmation message
 * @returns {Promise<boolean>} Promise that resolves to true if confirmed, false if cancelled
 */
export function confirm(message) {
    return new Promise((resolve) => {
        const id = shortid.generate()
        setConfirmationToast({
            id,
            message,
            onConfirm: () => {
                clearConfirmationToast()
                resolve(true)
            },
            onCancel: () => {
                clearConfirmationToast()
                resolve(false)
            }
        })
    })
}

/**
 * Notification API object
 */
const notify = {
    info,
    success,
    error,
    refusal,
    fatal,
    confirm
}

export default notify
